import { getEverLoggedIn } from 'dibs-cookie-jar';
import { sessionStorage } from 'dibs-browser-storage';
import { VisitCountModel } from 'dibs-visit-tracking/exports/visitCountModel';
import {
    KEY_REG_PROMPT_COUNT,
    KEY_LAST_REG_PROMPT_SESSION,
    KEY_SINCE_REG_PROMPT,
    KEY_SESSION_COUNT,
    KEY_REG_PROMPT_LIMIT,
    KEY_SESSION_STORAGE,
    VISITS_PER_SESSION_LOGIN_PROMPT,
} from 'dibs-visit-tracking/exports/visitCountConstants';
import { canVisitorBePromptedLogin } from '../notificationQueue/globalNotifications/helpers/canVisitorBePromptedLogin';
import { wasUserRedirectedByEmailLoginLink } from '../notificationQueue/globalNotifications/helpers/wasUserRedirectedByEmailLoginLink';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';
import { gaLabels } from '../authModal/postAuth/constants';
import authModalLoader from '../utils/AuthModalLoader';
import { autoPromptRegistration } from '../notificationQueue/globalNotifications/helpers/postAuthLoader';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import { LOGIN, PAGEVIEW_LIMIT_FLOW } from '../authModal/authentication/authFlowConstants';

const { EVENT_PROMO_INTERACTION } = eventNameConstants;
const { INTERACTION_TYPE_USER_LOGIN, INTERACTION_TYPE_USER_TYPE_SELECTION } =
    interactionTypeConstants;
const { STEP_INTERACTION_AUTO_MODAL_DISPLAYED } = stepInteractionConstants;

type VisitCountModelType = {
    get: (key: string) => number;
    set: (key: string, value: number | string) => void;
    increment: (key: string) => void;
    save: () => void;
};

const MODAL_LIMIT = 3;
const DWELLED_ON_PAGE_KEY = 'dwelledOnPage' as const;
const TEST_NAME = 'dwellTimeSamePage' as const;

export const isDwellTimeSamePageVariant = (): boolean =>
    getAbTestV2(TEST_NAME)?.variant === 'variant';

export const trackDwellTimeSamePageAbTestVariant = (): void => {
    trackAbTestV2Variant(TEST_NAME);
};

export const getDwelledOnPage = (): boolean => !!sessionStorage.getItem(DWELLED_ON_PAGE_KEY);

export const setDwelledOnPageFlag = (): void => {
    sessionStorage.setItem(DWELLED_ON_PAGE_KEY, 'true');
};

export const handleConsecutiveModals = ({
    visitCountModel,
}: {
    visitCountModel: VisitCountModelType;
}): void => {
    const sessionVisitCount = visitCountModel.get(KEY_SESSION_STORAGE);
    const lastRegPromptSession = visitCountModel.get(KEY_LAST_REG_PROMPT_SESSION);

    const isCorrectSession =
        sessionVisitCount >= lastRegPromptSession + VISITS_PER_SESSION_LOGIN_PROMPT;
    const isModalLimitReached = visitCountModel.get(KEY_REG_PROMPT_COUNT) === MODAL_LIMIT;

    if (
        //prompt consecutive modals only for unregistered users
        !getEverLoggedIn(document.cookie) &&
        isCorrectSession &&
        !isModalLimitReached &&
        //prevents cases when modal is blocked for more than one session to be ignored
        visitCountModel.get(KEY_SESSION_COUNT) > visitCountModel.get(KEY_REG_PROMPT_LIMIT)
    ) {
        visitCountModel.set(KEY_REG_PROMPT_LIMIT, visitCountModel.get(KEY_SESSION_COUNT));
        visitCountModel.save();
    }
};

export const shouldPromptLoginRegistration = (
    visitCountModel: VisitCountModelType,
    overrideDwelledOnPage: boolean
): boolean => {
    if (wasUserRedirectedByEmailLoginLink()) {
        return false;
    }

    if (visitCountModel.get(KEY_SINCE_REG_PROMPT) < visitCountModel.get(KEY_SESSION_COUNT)) {
        return false;
    }

    if (overrideDwelledOnPage) {
        return true;
    }

    const didDwellOnPage = getDwelledOnPage();
    const isRegPromptLimit =
        visitCountModel.get(KEY_SESSION_COUNT) === visitCountModel.get(KEY_REG_PROMPT_LIMIT);

    return isRegPromptLimit || didDwellOnPage;
};

export const canAutoPromptUser = ({ overrideDwelledOnPage = false } = {}): boolean => {
    const visitCountModel = new VisitCountModel();

    handleConsecutiveModals({ visitCountModel });

    const isModalBlocked = !canVisitorBePromptedLogin();
    const shouldOpenModal = shouldPromptLoginRegistration(visitCountModel, overrideDwelledOnPage);
    if (shouldOpenModal && !isModalBlocked) {
        return true;
    } else if (shouldOpenModal && isModalBlocked) {
        visitCountModel.increment(KEY_REG_PROMPT_LIMIT);
        visitCountModel.save();
        return false;
    } else {
        return false;
    }
};

export const openLoginModal =
    () =>
    (dispatch: (action: { type: string }) => void): void => {
        const flow = PAGEVIEW_LIMIT_FLOW;
        const visitCountModel = new VisitCountModel();
        const neverLoggedIn = !getEverLoggedIn(document.cookie);

        const ga = {
            label:
                neverLoggedIn &&
                visitCountModel.get(KEY_SESSION_COUNT) !== visitCountModel.get(KEY_REG_PROMPT_LIMIT)
                    ? gaLabels.LABEL_DWELL_TIME_REACHED
                    : gaLabels.PAGEVIEW_LIMIT_REACHED,
        };

        visitCountModel.increment(KEY_REG_PROMPT_COUNT);
        visitCountModel.set(KEY_LAST_REG_PROMPT_SESSION, visitCountModel.get(KEY_SESSION_STORAGE));
        visitCountModel.set(KEY_SINCE_REG_PROMPT, 0);
        visitCountModel.save();

        const trackAutoModal = (interaction_type: string): void => {
            const label = 'auto pageview';
            trackEvent({
                category: 'promo interaction',
                action: 'auto modal displayed',
                label,
                eventName: EVENT_PROMO_INTERACTION,
                interaction_type,
                step_interaction_name: STEP_INTERACTION_AUTO_MODAL_DISPLAYED,
                trigger: label,
            });
        };

        if (neverLoggedIn) {
            trackAutoModal(INTERACTION_TYPE_USER_TYPE_SELECTION);
            trackAbTestV2Variant('mwRegistrationRedesign');
            autoPromptRegistration({ flow, launchOrigin: ga.label });
        } else {
            trackAutoModal(INTERACTION_TYPE_USER_LOGIN);
            authModalLoader.show({
                action: LOGIN,
                flow,
                ga,
                fireEntryGa: true,
            });
        }
        dispatch({ type: 'noop' });
    };
